import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/videos';

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
              <i className="ri-play-circle-fill text-background-50 text-lg w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="text-base font-bold text-foreground-950 whitespace-nowrap" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('siteName')}
              </span>
              <span className="text-xs text-foreground-600 whitespace-nowrap">{t('siteTagline')}</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" aria-label="主导航">
            <Link to="/" className="text-sm font-medium text-foreground-950 hover:text-primary-500 transition-colors whitespace-nowrap">
              {t('nav.home')}
            </Link>
            {categories.slice(0, 5).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                {cat.name}
              </Link>
            ))}
            <Link to="/about" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">
              {t('nav.about')}
            </Link>
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            {/* Search */}
            {searchOpen ? (
              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('nav.search')}
                  className="w-48 md:w-64 px-3 py-1.5 text-sm rounded-lg border border-background-300/60 bg-background-100 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/70 transition-colors"
                  aria-label="关闭搜索"
                >
                  <i className="ri-close-line text-foreground-700 w-4 h-4 flex items-center justify-center"></i>
                </button>
              </form>
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/70 transition-colors"
                aria-label="搜索"
              >
                <i className="ri-search-line text-foreground-700 w-4 h-4 flex items-center justify-center"></i>
              </button>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/70 transition-colors"
              aria-label="菜单"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-foreground-700 w-5 h-5 flex items-center justify-center`}></i>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/70 py-4">
            <nav className="flex flex-col gap-1" aria-label="移动端导航">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="px-3 py-2.5 text-sm font-medium text-foreground-950 hover:bg-background-200/70 rounded-lg transition-colors"
              >
                {t('nav.home')}
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-200/70 rounded-lg transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
              <Link
                to="/about"
                onClick={() => setMobileMenuOpen(false)}
                className="px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-200/70 rounded-lg transition-colors"
              >
                {t('nav.about')}
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}